import { RefObject, useEffect, useRef } from 'react';
import { useDashboardScreenStore } from './useDashboardScreenStore';

/** Радиус, в пределах которого позиция считается достигнутой (px). */
const SETTLE_EPSILON = 2;
/** Предел кадров восстановления (~0.66с при 60fps): страховка от бесконечной подгонки. */
const MAX_RESTORE_FRAMES = 40;

/**
 * Сохраняет и восстанавливает позицию вертикальной прокрутки дашборда, чтобы
 * возврат из карточки диалога возвращал пользователя к тому же месту экрана, а
 * не к его началу.
 *
 * Позиция пишется в транзиентный стор при прокрутке (троттлинг через
 * `requestAnimationFrame`) и восстанавливается один раз при монтировании, когда
 * контент отрисован (`ready`). Нижние карточки догружаются асинхронно и высота
 * страницы растёт, поэтому восстановление повторяется несколько кадров подряд —
 * пока позиция не «прилипнет» либо не выйдет отведённое время.
 *
 * @param contentRef ссылка на элемент внутри области прокрутки страницы
 * @param ready контент страницы отрисован (сводка загружена)
 */
export const useDashboardScrollRestore = (
  contentRef: RefObject<HTMLElement | null>,
  ready: boolean,
): void => {
  const setScrollTop = useDashboardScreenStore((s) => s.setScrollTop);
  const restoredRef = useRef(false);

  // Вьюпорт ScrollArea, в котором лежит контент страницы (см. PageContainer).
  const getViewport = (): HTMLElement | null =>
    (contentRef.current?.closest('[data-radix-scroll-area-viewport]') as HTMLElement | null) ?? null;

  // Сохранение позиции при прокрутке.
  useEffect(() => {
    if (!ready) return;
    const viewport = getViewport();
    if (!viewport) return;
    let rafId = 0;
    let scheduled = false;
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      rafId = requestAnimationFrame(() => {
        scheduled = false;
        setScrollTop(viewport.scrollTop);
      });
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Однократное восстановление позиции при монтировании после отрисовки контента.
  useEffect(() => {
    if (restoredRef.current || !ready) return;
    const viewport = getViewport();
    if (!viewport) return;
    restoredRef.current = true;
    const target = useDashboardScreenStore.getState().scrollTop;
    if (target <= 0) return;
    let frame = 0;
    let rafId = 0;
    const apply = () => {
      viewport.scrollTop = target;
      frame += 1;
      // Останавливаемся, когда позиция достигнута (контент достаточно высок) или вышло время.
      if (Math.abs(viewport.scrollTop - target) <= SETTLE_EPSILON || frame >= MAX_RESTORE_FRAMES) return;
      rafId = requestAnimationFrame(apply);
    };
    rafId = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
};
